import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useClientTranslation } from '@/lib/core/i18n';
import { useCartStore, useLocaleStore } from '@/lib/core/stores';
import { formatAmount, getImageUrl, LinkRouteUrl } from '@/lib/core/utils';
import { useShopSections } from '@/lib/core/shop/sections';
import { useApp } from '@/lib/core/providers/appProvider';
import { getShopCheckoutHref, useShoppingBagContent } from '@/services/platform/data/shop';
import { ScreenHead } from '@/components/shared/screen/head';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { GiftMessageSheet } from './GiftMessageSheet';
import type { BagPriceChangeState } from '../checkout/ShopCheckoutScreen';
import type {
  CartProductItemDto,
  TitleRteItemElementContentModel,
  TitleRteItemPropertiesModel,
} from '@/services/platform/api/model';

/**
 * Shopping-bag page. Lists items, the gifting card, dynamic totals, useful-info
 * accordion, and the sticky continue CTA. State comes from `useCartStore`,
 * which mirrors the `/me/cart` API.
 */
export function ShopBagScreen() {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locale = useLocaleStore(state => state.locale);
  const localeSegment = locale?.split('-')[0] ?? 'en';
  const priceChangeState = location.state as BagPriceChangeState | undefined;

  const items = useCartStore(state => state.items);
  const itemCount = useCartStore(state => state.itemCount);
  const totalAmount = useCartStore(state => state.totalAmount);
  const shipping = useCartStore(state => state.shipping);
  const discount = useCartStore(state => state.discount);
  const currencyCode = useCartStore(state => state.currencyCode);
  const giftMessage = useCartStore(state => state.giftMessage);
  const setGiftMessage = useCartStore(state => state.setGiftMessage);
  const removeItem = useCartStore(state => state.removeItem);
  const hydrate = useCartStore(state => state.hydrate);
  const isHydrated = useCartStore(state => state.isHydrated);

  const [isGiftSheetOpen, setGiftSheetOpen] = useState(false);
  /** Set when the continue CTA finds no `shopCheckout` mapping to navigate to. */
  const [checkoutUnavailable, setCheckoutUnavailable] = useState(false);
  const { slugs: shopSectionSlugs } = useShopSections();
  const { appSettings } = useApp();
  const giftBoxImageUrl = getImageUrl(appSettings?.properties?.giftBoxImage?.[0]);
  const { properties: bagCms } = useShoppingBagContent();

  // CMS `usefulInformation` titleRteItem blocks; the hardcoded accordion below
  // stays as fallback while the CMS list is empty.
  const usefulInfoItems = (bagCms?.usefulInformation?.items ?? [])
    .map(block => (block.content as TitleRteItemElementContentModel | undefined)?.properties)
    .filter((props): props is TitleRteItemPropertiesModel => Boolean(props?.title));

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  const subtotal = items.reduce((sum, item) => sum + (item.totalPrice ?? 0), 0);
  const formattedTotal = formatAmount(totalAmount || subtotal, currencyCode, locale ?? 'en-GB');
  const formattedSubtotal = formatAmount(subtotal, currencyCode, locale ?? 'en-GB');
  const formattedShipping = formatAmount(shipping, currencyCode, locale ?? 'en-GB');
  const formattedDiscount = formatAmount(discount, currencyCode, locale ?? 'en-GB');

  const goBack = () => navigate(-1);
  const goContinue = () => {
    const checkoutHref = getShopCheckoutHref(appSettings);
    if (!checkoutHref) {
      /*
       * No authored checkout page means there is nowhere to take payment, and saying so beats
       * guessing. The literal `/{locale}/shop/checkout` that used to stand here worked right up
       * until an editor renamed the node, and then sent the customer to a 404 — a missing mapping
       * reported as a broken shop.
       */
      console.warn('ShopBagScreen: no shopCheckout route configured in app settings.');
      setCheckoutUnavailable(true);
      return;
    }
    setCheckoutUnavailable(false);
    navigate(`${checkoutHref}/delivery`);
  };
  const goShopNow = () => {
    const shopHref = LinkRouteUrl(appSettings?.properties?.shop?.route);
    navigate(shopHref ?? `/${localeSegment}/shop`);
  };
  const isEmpty = isHydrated && items.length === 0;

  /*
   * The cart has no section context, so the first CMS shop section is used
   * for the route segment.
   */
  const openItem = (item: CartProductItemDto) => {
    if (!item.productId) return;
    const section = shopSectionSlugs[0] ?? 'women';
    navigate(`/${localeSegment}/shop/${section}/discover/product_${item.productId}`);
  };

  return (
    <div className="pt-safe-area relative w-full pb-[calc((5.5rem+env(safe-area-inset-bottom))+68px)]">
      <ScreenHead
        title={bagCms?.shoppingBagTitle || t('App.Shop.ShoppingBagTitle')}
        onBack={goBack}
        ariaLabel={t('App.Labels.GoBack')}
        className="border-jncquoi-neutral-200 h-15 border-b-[0.5px]"
        rightActions={
          <div className="flex h-11 items-center justify-center gap-1 px-2">
            {itemCount > 0 && (
              <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] leading-none font-semibold">
                {itemCount}
              </span>
            )}
            <Icon
              name="shopping-bag"
              width={24}
              height={24}
              strokeWidth={1.5}
              className="text-jncquoi-neutral-1000"
            />
          </div>
        }
      />

      {priceChangeState?.priceChangeNotice && (
        <div className="bg-jncquoi-neutral-50 flex flex-col gap-1 px-4 py-3">
          <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-xs font-normal tracking-wide">
            {t('App.Shop.BagUpdatedTitle')}
          </p>
          <p className="font-pp-neue-montreal text-jncquoi-neutral-800 text-xs font-normal tracking-wide">
            {t('App.Shop.BagPricesChangedMessage')}
          </p>
        </div>
      )}

      {checkoutUnavailable && (
        <div className="bg-jncquoi-neutral-50 flex flex-col gap-1 px-4 py-3" role="alert">
          <p className="font-pp-neue-montreal text-jncquoi-neutral-800 text-xs font-normal tracking-wide">
            {t('App.Shop.CheckoutUnavailable')}
          </p>
        </div>
      )}

      {!isHydrated ? (
        <SpinLoading wrapperClassName="min-h-[50vh]" />
      ) : isEmpty ? (
        <ShopBagEmptyState onShopNow={goShopNow} />
      ) : (
        <>
          <ul className="flex flex-col">
            {items.map(item => (
              <BagItemRow
                key={item.variantId}
                item={item}
                locale={locale ?? 'en-GB'}
                currencyCode={currencyCode}
                previousUnitPrice={
                  item.variantId ? priceChangeState?.previousUnitPrices?.[item.variantId] : undefined
                }
                onOpen={() => openItem(item)}
                onRemove={() => item.variantId && removeItem(item.variantId)}
                removeLabel={t('App.Shop.RemoveItem')}
              />
            ))}
            {giftMessage && (
              <GiftBoxRow
                imageUrl={giftBoxImageUrl}
                title={t('App.Shop.GiftBoxItemTitle')}
                subtitle={t('App.Shop.GiftBagSubtitle')}
                description={t('App.Shop.PersonalMessageIncluded')}
                freeLabel={t('App.Shop.Free')}
                onOpen={() => setGiftSheetOpen(true)}
                onRemove={() => setGiftMessage('')}
                removeLabel={t('App.Shop.RemoveItem')}
              />
            )}
          </ul>

          {!giftMessage && (
            <section className="px-4 py-6">
              <p className="font-pp-neue-montreal text-jncquoi-neutral-800 text-sm leading-none font-normal tracking-wide uppercase">
                {bagCms?.giftBoxTitle || t('App.Shop.GiftingPackaging')}
              </p>
              <button
                type="button"
                onClick={() => setGiftSheetOpen(true)}
                className="mt-4 flex w-full items-center gap-4 p-4 text-left"
              >
                <div
                  className="bg-jncquoi-neutral-100 relative size-16 shrink-0 overflow-hidden"
                  aria-hidden="true"
                >
                  {giftBoxImageUrl && (
                    <img
                      src={giftBoxImageUrl}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-xs leading-none font-semibold tracking-wide">
                    {bagCms?.orderAsAGift || t('App.Shop.OrderAsGift')}
                  </p>
                  <p className="font-pp-neue-montreal text-jncquoi-neutral-700 mt-1.5 text-xs leading-relaxed tracking-wide">
                    {bagCms?.addPersonalTouch || t('App.Shop.OrderAsGiftDescription')}
                  </p>
                </div>
                <Icon
                  name="nav-arrow-right"
                  width={20}
                  height={20}
                  strokeWidth={1.5}
                  className="text-jncquoi-neutral-1000 shrink-0"
                />
              </button>
            </section>
          )}

          <section className="px-4 py-6">
            <SummaryRow label={t('App.Shop.Subtotal')} value={formattedSubtotal} />
            <SummaryRow
              label={t('App.Shop.Shipping')}
              hint={t('App.Shop.ShippingCalculatedNote')}
              value={formattedShipping}
            />
            <SummaryRow label={t('App.Shop.AppliedDiscount')} value={formattedDiscount} />
            <SummaryRow label={t('App.Shop.Total')} value={formattedTotal} strong />
          </section>

          <section className="px-4 py-6">
            <p className="font-pp-neue-montreal text-jncquoi-neutral-800 mb-4 text-sm leading-none font-normal tracking-wide uppercase">
              {bagCms?.usefulInformationTitle || t('App.Shop.UsefulInformation')}
            </p>
            <Accordion type="multiple">
              {usefulInfoItems.length > 0 ? (
                usefulInfoItems.map((item, index) => (
                  <AccordionItem key={index} value={`useful-${index}`}>
                    <AccordionTrigger>{item.title}</AccordionTrigger>
                    <AccordionContent className="font-pp-neue-montreal text-jncquoi-neutral-1000 pb-3 text-[13px] leading-relaxed tracking-[0.25px]">
                      <div dangerouslySetInnerHTML={{ __html: item.text?.markup ?? '' }} />
                    </AccordionContent>
                  </AccordionItem>
                ))
              ) : (
                <>
                  <AccordionItem value="secured">
                    <AccordionTrigger>{t('App.Shop.SecuredPayment')}</AccordionTrigger>
                    <AccordionContent className="font-pp-neue-montreal text-jncquoi-neutral-1000 pb-3 text-[13px] leading-relaxed tracking-[0.25px]">
                      <p>—</p>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="delivery">
                    <AccordionTrigger>{t('App.Shop.DeliveryAccordion')}</AccordionTrigger>
                    <AccordionContent className="font-pp-neue-montreal text-jncquoi-neutral-1000 pb-3 text-[13px] leading-relaxed tracking-[0.25px]">
                      <p>—</p>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="returns">
                    <AccordionTrigger>{t('App.Shop.ReturnsExchanges')}</AccordionTrigger>
                    <AccordionContent className="font-pp-neue-montreal text-jncquoi-neutral-1000 pb-3 text-[13px] leading-relaxed tracking-[0.25px]">
                      <p>—</p>
                    </AccordionContent>
                  </AccordionItem>
                </>
              )}
            </Accordion>
          </section>

          {/*
           * Bottom padding clears the floating AppNavBar pill (fixed at
           * `bottom: 1.5rem + safe-area-inset-bottom`, `h-12` tall — see
           * components/layout/navBar/index.tsx) plus a small gap, so the button
           * doesn't render underneath it.
           */}
          <div className="bg-jncquoi-neutral-00 border-jncquoi-neutral-300 fixed right-0 bottom-0 left-0 z-30 border-t-[0.5px] px-4 pt-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))]">
            <div className="flex items-center gap-4">
              <Button
                variant="primary-solid"
                palette="dark"
                size="md"
                className="flex-1"
                disabled={items.length === 0 || !isHydrated}
                onClick={goContinue}
              >
                {bagCms?.continueBtnText || t('App.Shop.ContinueCheckout')}
              </Button>
              <div className="flex flex-col items-end">
                <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-sm leading-none font-semibold tracking-wide">
                  {formattedTotal}
                </span>
                <span className="font-pp-neue-montreal text-jncquoi-neutral-800 mt-1 text-xs leading-none tracking-wide">
                  {t('App.Shop.TaxesIncludedNote')}
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      <GiftMessageSheet
        open={isGiftSheetOpen}
        onOpenChange={setGiftSheetOpen}
        initialMessage={giftMessage}
        onSave={setGiftMessage}
        title={bagCms?.addYourGiftMessage}
        inputLabel={bagCms?.personalizeYourMessage}
        saveLabel={bagCms?.addBtnLabel}
        cancelLabel={bagCms?.cancelBtnLabel}
      />
    </div>
  );
}

interface ShopBagEmptyStateProps {
  onShopNow: () => void;
}

function ShopBagEmptyState({ onShopNow }: ShopBagEmptyStateProps) {
  const t = useClientTranslation();

  return (
    <>
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-2 px-4 text-center">
        <Icon
          name="warning-circle"
          width={28}
          height={28}
          strokeWidth={1.5}
          className="text-jncquoi-neutral-1000"
        />
        <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 mt-2 text-[12px] tracking-[0.5px] uppercase">
          {t('App.Shop.YourBagIsEmpty')}
        </p>
        <p className="font-pp-neue-montreal text-jncquoi-neutral-700 max-w-64 text-[14px] leading-normal tracking-[0.25px]">
          {t('App.Shop.BagEmptyDescription')}
        </p>
      </div>

      <div className="fixed right-0 bottom-0 left-0 z-30 px-4 pt-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))]">
        <Button variant="primary-solid" palette="dark" size="md" className="w-full" onClick={onShopNow}>
          {t('App.Shop.ShopNow')}
        </Button>
      </div>
    </>
  );
}

interface BagItemRowProps {
  item: CartProductItemDto;
  locale: string;
  currencyCode: string;
  /** Unit price before a checkout-time re-price (409 prices_changed); shown struck-through when it differs. */
  previousUnitPrice?: number | null;
  onOpen: () => void;
  onRemove: () => void;
  removeLabel: string;
}

function BagItemRow({
  item,
  locale,
  currencyCode,
  previousUnitPrice,
  onOpen,
  onRemove,
  removeLabel,
}: BagItemRowProps) {
  const t = useClientTranslation();

  const optionLabel = (name: string | null | undefined) => {
    const normalized = name?.trim().toLowerCase();
    if (normalized === 'size') return t('App.Shop.SizeLabel');
    if (normalized === 'colour' || normalized === 'color') return t('App.Shop.ColourLabel');
    return name;
  };

  const variantInfo = [
    ...(item.options
      ?.filter(option => option.value)
      .map(option => [optionLabel(option.name), option.value].filter(Boolean).join(' ')) ?? []),
    `${t('App.Shop.QuantityAbbr')}: ${item.quantity ?? 1}`,
  ].join(' • ');

  return (
    <li className="flex items-center gap-4 px-4 py-4">
      <button type="button" onClick={onOpen} className="flex min-w-0 flex-1 items-start gap-4 text-left">
        <div className="bg-jncquoi-neutral-100 relative h-24 w-20 shrink-0 overflow-hidden">
          {item.imageUrl && (
            <img
              src={item.imageUrl}
              alt={item.name ?? ''}
              className="absolute inset-0 h-full w-full object-cover"
            />
          )}
        </div>
        <div className="flex h-24 min-w-0 flex-1 flex-col justify-between">
          <div className="flex flex-col gap-1">
            {item.vendor && (
              <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[10px] leading-none font-semibold tracking-wide uppercase">
                {item.vendor}
              </span>
            )}
            {item.name && (
              <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-xs leading-relaxed tracking-wide">
                {item.name}
              </span>
            )}
            <span className="font-pp-neue-montreal text-jncquoi-neutral-700 text-xs leading-relaxed tracking-wide">
              {variantInfo}
            </span>
          </div>
          {previousUnitPrice != null && previousUnitPrice !== item.unitPrice ? (
            <span className="flex items-baseline gap-2">
              <span className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[12px] leading-none font-normal line-through">
                {formatAmount(previousUnitPrice, currencyCode, locale)}
              </span>
              <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] leading-none font-semibold">
                {formatAmount(item.totalPrice ?? item.unitPrice ?? 0, currencyCode, locale)}
              </span>
            </span>
          ) : (
            <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] leading-none font-semibold">
              {formatAmount(item.totalPrice ?? item.unitPrice ?? 0, currencyCode, locale)}
            </span>
          )}
        </div>
      </button>
      <button
        type="button"
        aria-label={removeLabel}
        onClick={onRemove}
        className="flex size-11 items-center justify-center"
      >
        <Icon name="trash" width={24} height={24} strokeWidth={1.5} className="text-jncquoi-neutral-1000" />
      </button>
    </li>
  );
}

interface GiftBoxRowProps {
  imageUrl: string | undefined;
  title: string;
  subtitle: string;
  description: string;
  freeLabel: string;
  onOpen: () => void;
  onRemove: () => void;
  removeLabel: string;
}

function GiftBoxRow({
  imageUrl,
  title,
  subtitle,
  description,
  freeLabel,
  onOpen,
  onRemove,
  removeLabel,
}: GiftBoxRowProps) {
  return (
    <li className="flex items-center gap-4 px-4 py-4">
      <button type="button" onClick={onOpen} className="flex min-w-0 flex-1 items-start gap-4 text-left">
        <div className="bg-jncquoi-neutral-100 relative h-24 w-20 shrink-0 overflow-hidden">
          {imageUrl && <img src={imageUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />}
        </div>
        <div className="flex h-24 min-w-0 flex-1 flex-col justify-between">
          <div className="flex flex-col gap-1">
            <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[10px] leading-none font-semibold tracking-wide uppercase">
              {title}
            </span>
            <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-xs leading-relaxed tracking-wide">
              {subtitle}
            </span>
            <span className="font-pp-neue-montreal text-jncquoi-neutral-700 text-xs leading-relaxed tracking-wide">
              {description}
            </span>
          </div>
          <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] leading-none font-semibold uppercase">
            {freeLabel}
          </span>
        </div>
      </button>
      <button
        type="button"
        aria-label={removeLabel}
        onClick={onRemove}
        className="flex size-11 items-center justify-center"
      >
        <Icon name="trash" width={24} height={24} strokeWidth={1.5} className="text-jncquoi-neutral-1000" />
      </button>
    </li>
  );
}

interface SummaryRowProps {
  label: string;
  hint?: string;
  value: string;
  strong?: boolean;
}

function SummaryRow({ label, hint, value, strong }: SummaryRowProps) {
  return (
    <div className="mb-3 flex items-baseline justify-between">
      <span
        className={
          strong
            ? 'font-pp-neue-montreal text-jncquoi-neutral-1000 text-sm leading-none font-semibold tracking-wide'
            : 'font-pp-neue-montreal text-jncquoi-neutral-700 text-sm leading-none font-normal tracking-wide'
        }
      >
        {label}
        {hint && <span className="text-jncquoi-neutral-700 ml-1 text-xs tracking-wide">{hint}</span>}
      </span>
      <span
        className={
          strong
            ? 'font-inter text-jncquoi-neutral-1000 text-base leading-none font-semibold tracking-tight'
            : 'font-pp-neue-montreal text-jncquoi-neutral-1000 text-sm leading-none font-normal tracking-wide'
        }
      >
        {value}
      </span>
    </div>
  );
}
